import {
  createTaskEditorActionComponent,
  createTaskEditorBlock,
  createTaskEditorButtonOption,
  createTaskEditorDescriptionComponent,
  createTaskEditorTextInputField,
  deriveTaskEditorBlockType,
  isTaskEditorDescriptionComponentType,
  TaskEditorActionComponent,
  TaskEditorActionComponentType,
  TaskEditorBlock,
  TaskEditorBlockType,
  TaskEditorButtonOption,
  TaskEditorComponentType,
  TaskEditorDescriptionComponent,
  TaskEditorDescriptionComponentType,
  TaskEditorTextInputField,
  TaskEditorTypographyStyle,
} from "./taskEditorUtils";

// ─── 헬퍼 ─────────────────────────────────────────────────────────────────

function replaceBlock(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  updater: (block: TaskEditorBlock) => TaskEditorBlock
): TaskEditorBlock[] {
  if (blockIndex < 0 || blockIndex >= blocks.length) return blocks;
  const target = blocks[blockIndex];
  const next = updater(target);
  if (next === target) return blocks;
  const out = [...blocks];
  out[blockIndex] = next;
  return out;
}

function replaceDescriptionComponent(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  descriptionIndex: number,
  updater: (
    component: TaskEditorDescriptionComponent
  ) => TaskEditorDescriptionComponent
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    if (descriptionIndex < 0 || descriptionIndex >= block.description.length) {
      return block;
    }
    const target = block.description[descriptionIndex];
    const next = updater(target);
    if (next === target) return block;
    const out = [...block.description];
    out[descriptionIndex] = next;
    return { ...block, description: out };
  });
}

function replaceAction(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  updater: (action: TaskEditorActionComponent) => TaskEditorActionComponent
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    if (!block.action) return block;
    const next = updater(block.action);
    if (next === block.action) return block;
    return { ...block, action: next };
  });
}

// ─── Block ────────────────────────────────────────────────────────────────

export function applyAddBlock(
  blocks: TaskEditorBlock[],
  insertionIndex?: number,
  type: TaskEditorBlockType = "body"
): TaskEditorBlock[] {
  const next = [...blocks];
  const idx =
    typeof insertionIndex === "number"
      ? Math.max(0, Math.min(insertionIndex, blocks.length))
      : blocks.length;
  next.splice(idx, 0, createTaskEditorBlock(type));
  return next;
}

export function applyRemoveBlock(
  blocks: TaskEditorBlock[],
  blockIndex: number
): TaskEditorBlock[] {
  if (blockIndex < 0 || blockIndex >= blocks.length) return blocks;
  if (blocks.length === 1) {
    // 마지막 블록 제거 시 빈 블록 1 개로 리셋
    return [createTaskEditorBlock()];
  }
  const next = [...blocks];
  next.splice(blockIndex, 1);
  return next;
}

// ─── Component (add / remove / type change) ──────────────────────────────

export function applyAddComponent(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  type: TaskEditorComponentType
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    if (isTaskEditorDescriptionComponentType(type)) {
      const newComponent = createTaskEditorDescriptionComponent(type);
      return { ...block, description: [...block.description, newComponent] };
    }
    // action — 이미 있으면 거부 (shape 으로 max-1 강제)
    if (block.action) return block;
    return { ...block, action: createTaskEditorActionComponent(type) };
  });
}

export function applyRemoveDescriptionComponent(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  descriptionIndex: number
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    if (descriptionIndex < 0 || descriptionIndex >= block.description.length) {
      return block;
    }
    // 첫 description (text 슬롯) 은 제거 불가
    if (descriptionIndex === 0) return block;
    const next = block.description.filter((_, i) => i !== descriptionIndex);
    return { ...block, description: next };
  });
}

export function applyRemoveActionComponent(
  blocks: TaskEditorBlock[],
  blockIndex: number
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    if (!block.action) return block;
    return { ...block, action: null };
  });
}

// 카테고리/sub-type 내 변경. cross-section 은 호출자가 add+remove 로 처리.
export function applyDescriptionComponentTypeChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  descriptionIndex: number,
  nextType: TaskEditorDescriptionComponentType
): TaskEditorBlock[] {
  // 첫 description 은 text 로 고정
  if (descriptionIndex === 0 && nextType !== "text") return blocks;

  return replaceBlock(blocks, blockIndex, (block) => {
    if (descriptionIndex < 0 || descriptionIndex >= block.description.length) {
      return block;
    }
    const current = block.description[descriptionIndex];
    if (current.type === nextType) return block;
    const replaced = migrateDescriptionComponentToType(current, nextType);
    const next = [...block.description];
    next[descriptionIndex] = replaced;
    return { ...block, description: next };
  });
}

export function applyActionComponentTypeChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  nextType: TaskEditorActionComponentType
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    if (!block.action) return block;
    if (block.action.type === nextType) return block;
    const replaced = migrateActionComponentToType(block.action, nextType);
    return { ...block, action: replaced };
  });
}

// 같은 reference sub-category 안에서 type 만 바꾸는 경우 일부 속성 보존.
function migrateDescriptionComponentToType(
  current: TaskEditorDescriptionComponent,
  nextType: TaskEditorDescriptionComponentType
): TaskEditorDescriptionComponent {
  const fresh = createTaskEditorDescriptionComponent(nextType);
  // reference 내부: preset_text 보존 시도
  if (
    (current.type === "reference_automatic_thought" ||
      current.type === "reference_coach_preset") &&
    (nextType === "reference_automatic_thought" ||
      nextType === "reference_coach_preset")
  ) {
    if (nextType === "reference_coach_preset") {
      return {
        type: "reference_coach_preset",
        preset_text:
          current.type === "reference_coach_preset" ? current.preset_text : "",
      };
    }
    return { type: "reference_automatic_thought" };
  }
  return fresh;
}

// 같은 button category 안에서 type 만 바꾸는 경우 옵션 보존.
function migrateActionComponentToType(
  current: TaskEditorActionComponent,
  nextType: TaskEditorActionComponentType
): TaskEditorActionComponent {
  const fresh = createTaskEditorActionComponent(nextType);
  const isCurrentButton =
    current.type === "button_default" ||
    current.type === "button_quiz" ||
    current.type === "button_branch";
  const isNextButton =
    nextType === "button_default" ||
    nextType === "button_quiz" ||
    nextType === "button_branch";

  if (isCurrentButton && isNextButton) {
    const options =
      "options" in current && Array.isArray(current.options)
        ? current.options
        : [];
    const fallbackOptions = [
      createTaskEditorButtonOption(),
      createTaskEditorButtonOption(),
    ];
    if (nextType === "button_default") {
      return {
        type: "button_default",
        allow_multiple_selection:
          current.type === "button_default"
            ? current.allow_multiple_selection
            : true,
        options: options.length > 0 ? options : fallbackOptions,
      };
    }
    if (nextType === "button_quiz") {
      return {
        type: "button_quiz",
        options: options.length > 0 ? options : fallbackOptions,
      };
    }
    if (nextType === "button_branch") {
      return {
        type: "button_branch",
        options: options.length > 0 ? options : fallbackOptions,
      };
    }
  }
  return fresh;
}

// ─── text / image content ────────────────────────────────────────────────

export function applyDescriptionTextContentChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  descriptionIndex: number,
  content: string
): TaskEditorBlock[] {
  return replaceDescriptionComponent(
    blocks,
    blockIndex,
    descriptionIndex,
    (c) => {
      if (c.type !== "text" && c.type !== "image") return c;
      if (c.content === content) return c;
      return { ...c, content };
    }
  );
}

export function applyTypographyStyleChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  descriptionIndex: number,
  style: TaskEditorTypographyStyle
): TaskEditorBlock[] {
  return replaceBlock(blocks, blockIndex, (block) => {
    if (descriptionIndex < 0 || descriptionIndex >= block.description.length) {
      return block;
    }
    const target = block.description[descriptionIndex];
    if (target.type !== "text") return block;
    if (target.typography_style === style) return block;
    const nextDescription = [...block.description];
    nextDescription[descriptionIndex] = { ...target, typography_style: style };
    // 첫 description 의 typography_style 변경은 block.type 과 동기화한다.
    const nextType: TaskEditorBlockType =
      descriptionIndex === 0
        ? deriveTaskEditorBlockType({ description: nextDescription })
        : block.type;
    if (nextType === block.type) {
      return { ...block, description: nextDescription };
    }
    return { ...block, type: nextType, description: nextDescription };
  });
}

export function applyReferencePresetTextChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  descriptionIndex: number,
  text: string
): TaskEditorBlock[] {
  return replaceDescriptionComponent(
    blocks,
    blockIndex,
    descriptionIndex,
    (c) => {
      if (c.type !== "reference_coach_preset") return c;
      if (c.preset_text === text) return c;
      return { ...c, preset_text: text };
    }
  );
}

// ─── text_input fields (action) ──────────────────────────────────────────

function withTextInput(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  updater: (fields: TaskEditorTextInputField[]) => TaskEditorTextInputField[]
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (a.type !== "text_input") return a;
    const nextFields = updater(a.fields);
    if (nextFields === a.fields) return a;
    return { ...a, fields: nextFields };
  });
}

export function applyAddTextInputField(
  blocks: TaskEditorBlock[],
  blockIndex: number
): TaskEditorBlock[] {
  return withTextInput(blocks, blockIndex, (fields) => [
    ...fields,
    createTaskEditorTextInputField(),
  ]);
}

export function applyRemoveTextInputField(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  fieldIndex: number
): TaskEditorBlock[] {
  return withTextInput(blocks, blockIndex, (fields) => {
    if (fields.length <= 1) return fields;
    if (fieldIndex < 0 || fieldIndex >= fields.length) return fields;
    return fields.filter((_, i) => i !== fieldIndex);
  });
}

export function applyTextInputFieldChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  fieldIndex: number,
  key: "label" | "placeholder" | "help_text",
  value: string
): TaskEditorBlock[] {
  return withTextInput(blocks, blockIndex, (fields) => {
    if (fieldIndex < 0 || fieldIndex >= fields.length) return fields;
    const target = fields[fieldIndex];
    const currentValue = target[key];
    const nextField: TaskEditorTextInputField = { ...target };
    if (value === "") {
      delete nextField[key];
    } else {
      nextField[key] = value;
    }
    if (currentValue === nextField[key]) return fields;
    const out = [...fields];
    out[fieldIndex] = nextField;
    return out;
  });
}

// optional 토글 — 기본 필수. true 일 때만 저장하고 false 면 키 삭제
// (button_quiz 의 is_correct 와 동일 컨벤션).
export function applyTextInputFieldOptionalToggle(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  fieldIndex: number,
  optional: boolean
): TaskEditorBlock[] {
  return withTextInput(blocks, blockIndex, (fields) => {
    if (fieldIndex < 0 || fieldIndex >= fields.length) return fields;
    const target = fields[fieldIndex];
    if ((target.optional ?? false) === optional) return fields;
    const nextField: TaskEditorTextInputField = { ...target };
    if (optional) {
      nextField.optional = true;
    } else {
      delete nextField.optional;
    }
    const out = [...fields];
    out[fieldIndex] = nextField;
    return out;
  });
}

// ─── button options (action) ─────────────────────────────────────────────

type ButtonAction = Extract<
  TaskEditorActionComponent,
  { type: "button_default" | "button_quiz" | "button_branch" }
>;

function isButtonAction(a: TaskEditorActionComponent): a is ButtonAction {
  return (
    a.type === "button_default" ||
    a.type === "button_quiz" ||
    a.type === "button_branch"
  );
}

function withButtonOptions(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  updater: (options: TaskEditorButtonOption[]) => TaskEditorButtonOption[]
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (!isButtonAction(a)) return a;
    const nextOptions = updater(a.options);
    if (nextOptions === a.options) return a;
    return { ...a, options: nextOptions } as ButtonAction;
  });
}

export function applyAddButtonOption(
  blocks: TaskEditorBlock[],
  blockIndex: number
): TaskEditorBlock[] {
  return withButtonOptions(blocks, blockIndex, (options) => [
    ...options,
    createTaskEditorButtonOption(),
  ]);
}

export function applyRemoveButtonOption(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  optionIndex: number
): TaskEditorBlock[] {
  return withButtonOptions(blocks, blockIndex, (options) => {
    if (optionIndex < 0 || optionIndex >= options.length) return options;
    const next = options.filter((_, i) => i !== optionIndex);
    return next.length > 0
      ? next
      : [createTaskEditorButtonOption(), createTaskEditorButtonOption()];
  });
}

export function applyButtonOptionTextChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  optionIndex: number,
  text: string
): TaskEditorBlock[] {
  return withButtonOptions(blocks, blockIndex, (options) => {
    if (optionIndex < 0 || optionIndex >= options.length) return options;
    if (options[optionIndex].text === text) return options;
    const out = [...options];
    out[optionIndex] = { ...out[optionIndex], text };
    return out;
  });
}

export function applyButtonQuizOptionCorrectToggle(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  optionIndex: number,
  isCorrect: boolean
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (a.type !== "button_quiz") return a;
    if (optionIndex < 0 || optionIndex >= a.options.length) return a;
    const current = a.options[optionIndex];
    if ((current.is_correct ?? false) === isCorrect) return a;
    const nextOption: TaskEditorButtonOption = { ...current };
    if (isCorrect) {
      nextOption.is_correct = true;
    } else {
      delete nextOption.is_correct;
    }
    const next = [...a.options];
    next[optionIndex] = nextOption;
    return { ...a, options: next };
  });
}

export function applyButtonBranchOptionSequenceChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  optionIndex: number,
  key: "show_from" | "show_to" | "restart_from" | "hide_from",
  value: number | undefined
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (a.type !== "button_branch") return a;
    if (optionIndex < 0 || optionIndex >= a.options.length) return a;
    const current = a.options[optionIndex];
    if ((current[key] ?? undefined) === value) return a;
    const nextOption: TaskEditorButtonOption = { ...current };
    if (typeof value === "number") {
      nextOption[key] = value;
    } else {
      delete nextOption[key];
    }
    const next = [...a.options];
    next[optionIndex] = nextOption;
    return { ...a, options: next };
  });
}

export function applyButtonDefaultAllowMultipleChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  allow: boolean
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (a.type !== "button_default") return a;
    if (a.allow_multiple_selection === allow) return a;
    return { ...a, allow_multiple_selection: allow };
  });
}

// ─── audio (action) ───────────────────────────────────────────────────────

// extra_audio_url 은 선택값이라 빈 문자열이면 키를 삭제(is_correct·optional 컨벤션).
// text / artwork / audio_url 은 항상 보존되는 문자열.
export function applyAudioFieldChange(
  blocks: TaskEditorBlock[],
  blockIndex: number,
  key: "artwork" | "audio_url" | "extra_audio_url",
  value: string
): TaskEditorBlock[] {
  return replaceAction(blocks, blockIndex, (a) => {
    if (a.type !== "audio") return a;
    if (key === "extra_audio_url") {
      if ((a.extra_audio_url ?? "") === value) return a;
      const next = { ...a };
      if (value === "") {
        delete next.extra_audio_url;
      } else {
        next.extra_audio_url = value;
      }
      return next;
    }
    if (a[key] === value) return a;
    return { ...a, [key]: value };
  });
}
